import { useMemo } from "react";
import { T, fontMono } from "../theme";
import { fmtMoney, fmtWeek, fyWeek, isoDate, parseDate } from "../engine/dates";

/**
 * The card, week by week.
 *
 * Same shape as the cash flow grid, because it answers the same question about
 * a different account: what goes on, what comes off, and where does that leave
 * the balance. The difference is the bottom line — a bank balance is measured
 * against an overdraft, a card balance against a limit, and the limit is the
 * thing that stops the spend happening at all.
 *
 * Charges are grouped by what they are rather than listed one by one: a media
 * budget spread over ten weeks is one arrangement, not ten facts.
 */
export default function AmexGrid({ weeks, charges = [], amex, docNames = {} }) {
  const { rows, totals, repayments, forced, balances, limit } = useMemo(() => {
    const limit = Number(amex?.limit) || 0;
    const ends = weeks.map((w) => w.end);
    const weekIndex = (d) => {
      for (let i = 0; i < ends.length; i++) if (d <= ends[i]) return i;
      return -1;
    };

    // One row per thing being charged — keyed on the document it belongs to
    // where there is one, so eleven charges called "Meta" separate into the
    // pieces of work paying for them instead of collapsing into one line.
    const byKey = {};
    for (const c of charges) {
      const d = parseDate(c.charge_date);
      if (!d) continue;
      const i = weekIndex(d);
      if (i < 0) continue;
      const docKey = c.docSource && c.docId ? `${c.docSource}:${c.docId}` : null;
      const key = docKey || (c.description || "AMEX charge").trim();
      const doc = docKey ? docNames[docKey] : null;
      byKey[key] = byKey[key] || {
        key,
        docKey,
        label: (c.description || "AMEX charge").trim(),
        client: doc?.client || null,
        docNo: doc?.docNo || null,
        docKind: doc?.kind || null,
        gone: !!docKey && !doc,
        cells: Array(weeks.length).fill(0),
      };
      byKey[key].cells[i] += Number(c.amount) || 0;
    }
    const rows = Object.values(byKey).sort(
      (a, b) => b.cells.reduce((s, x) => s + x, 0) - a.cells.reduce((s, x) => s + x, 0)
    );

    const totals = weeks.map((_, i) => rows.reduce((s, r) => s + r.cells[i], 0));

    const repayments = Array(weeks.length).fill(0);
    const forced = Array(weeks.length).fill(0);
    for (const p of amex?.payments || []) {
      const i = weekIndex(p.date instanceof Date ? p.date : parseDate(p.date));
      if (i < 0) continue;
      if (p.enforced) forced[i] += Number(p.amount) || 0;
      else repayments[i] += Number(p.amount) || 0;
    }

    // The balance at the end of each week, read off the ledger rather than
    // recomputed — one definition of what is owed, not two.
    const timeline = (amex?.timeline || [])
      .map((t) => ({ ...t, d: parseDate(t.date) }))
      .filter((t) => t.d)
      .sort((a, b) => a.d - b.d);
    let ti = 0;
    let running = timeline.length ? timeline[0].balance : 0;
    const balances = weeks.map((w) => {
      while (ti < timeline.length && timeline[ti].d <= w.end) {
        running = timeline[ti].balance;
        ti++;
      }
      return running;
    });

    return { rows, totals, repayments, forced, balances, limit };
  }, [weeks, charges, amex, docNames]);

  if (!weeks.length) return null;

  const num = {
    padding: "6px 10px",
    textAlign: "right",
    fontFamily: fontMono,
    fontSize: 11,
    whiteSpace: "nowrap",
  };
  const name = {
    padding: "6px 10px",
    fontSize: 12,
    whiteSpace: "nowrap",
    position: "sticky",
    left: 0,
    background: T.panel,
    zIndex: 1,
  };

  return (
    <div style={{ background: T.panel, borderRadius: 10, overflowX: "auto", marginTop: 8 }}>
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead>
          <tr>
            <th style={{ ...name, textAlign: "left", fontSize: 11, color: T.muted, fontWeight: 500 }}>
              WEEK
            </th>
            {weeks.map((w) => (
              <th
                key={isoDate(w.start)}
                style={{
                  ...num,
                  fontFamily: "inherit",
                  fontWeight: 500,
                  color: w.isHistory ? T.muted : T.ink,
                  background: w.isHistory ? T.panelSoft : undefined,
                }}
              >
                {fmtWeek(w.start)}
                <div style={{ fontSize: 9, color: T.muted }}>W{fyWeek(w.start).week}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...name, fontStyle: "italic", fontSize: 11, color: T.muted }}>
              Charged to the card
            </td>
            <td colSpan={weeks.length} style={{ background: T.panelSoft }} />
          </tr>
          {rows.map((r) => (
            <tr key={r.label} style={{ borderTop: `1px solid ${T.borderSoft}` }}>
              <td style={name} title={`${r.client ? `${r.client} · ` : ""}${r.label}`}>
                <div>{r.label.length > 30 ? `${r.label.slice(0, 29)}…` : r.label}</div>
                <div style={{ fontSize: 9, color: T.muted }}>
                  {r.client
                    ? `${r.client}${r.docNo ? ` · ${r.docKind} ${r.docNo}` : ""}`
                    : r.gone
                      ? "the document behind this has gone"
                      : "not attached to a document"}
                </div>
              </td>
              {r.cells.map((v, i) => (
                <td key={i} style={{ ...num, color: v ? T.cost : T.border }}>
                  {v ? fmtMoney(v) : "–"}
                </td>
              ))}
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td style={name} colSpan={weeks.length + 1}>
                <span style={{ fontSize: 12, color: T.muted }}>
                  Nothing is charged to the card in this window.
                </span>
              </td>
            </tr>
          )}

          <tr style={{ borderTop: `1px solid ${T.border}` }}>
            <td style={{ ...name, fontWeight: 600 }}>Total charged</td>
            {totals.map((v, i) => (
              <td key={i} style={{ ...num, fontWeight: 600, color: v ? T.cost : T.border }}>
                {v ? fmtMoney(v) : "–"}
              </td>
            ))}
          </tr>

          <tr>
            <td style={{ ...name, fontWeight: 600 }}>Paid off</td>
            {repayments.map((v, i) => (
              <td key={i} style={{ ...num, fontWeight: 600, color: v ? T.income : T.border }}>
                {v ? fmtMoney(v) : "–"}
              </td>
            ))}
          </tr>

          {forced.some((v) => v) && (
            <tr>
              <td style={{ ...name, fontWeight: 600, color: T.warn }}>
                Paid down to stay inside the limit
              </td>
              {forced.map((v, i) => (
                <td key={i} style={{ ...num, fontWeight: 600, color: v ? T.warn : T.border }}>
                  {v ? fmtMoney(v) : "–"}
                </td>
              ))}
            </tr>
          )}

          <tr style={{ borderTop: `1px solid ${T.border}` }}>
            <td style={{ ...name, fontWeight: 700 }}>Owed on the card</td>
            {balances.map((v, i) => {
              const over = limit > 0 && v > limit;
              return (
                <td
                  key={i}
                  title={
                    over
                      ? `${fmtMoney(v - limit)} over the ${fmtMoney(limit)} limit`
                      : limit
                        ? `${fmtMoney(limit - v)} of the limit still free`
                        : ""
                  }
                  style={{
                    ...num,
                    fontWeight: 700,
                    color: over ? T.cost : T.ink,
                    background: over ? "#FBEFE2" : undefined,
                  }}
                >
                  {fmtMoney(v)}
                </td>
              );
            })}
          </tr>
          {limit > 0 && (
            <tr>
              <td style={{ ...name, fontSize: 11, color: T.muted }}>Limit</td>
              {weeks.map((_, i) => (
                <td key={i} style={{ ...num, color: T.muted, fontSize: 10 }}>
                  {fmtMoney(limit)}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
